import moment from "moment";
import { toast } from "react-toastify";

export const showToast = (type: string, message: string) => {
  console.log("show toast...");

  const toastOptions: object = {
    position: "bottom-left",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    theme: "dark",
  };

  if (type === "success") {
    toast.success(message, toastOptions);
  } else {
    toast.error(message, toastOptions);
  }
};

export const isValidEmail = (email: string) => {
  var pattern = new RegExp(
    /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
  );
  return pattern.test(email);
};

export const isValidPhone = (phoneNumber: string) => {
  const phoneRegex =
    /^(?:\+?\d{1,3})?[-.\s]?(\(?\d{1,4}\)?[-.\s]?)?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,4}$/;
  return phoneRegex.test(phoneNumber);
};

// Anchor a react-phone-input-2 value to the US dial code (1). Without this, a
// leading local digit that matches another dial code (e.g. "7") makes the
// library switch the country (+7). Forcing a "1" prefix keeps it US (+1) so the
// typed digits are always treated as the local number.
export const normalizeUsPhone = (value: string | null | undefined): string => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits === "") {
    return "";
  }
  return digits.startsWith("1") ? digits : "1" + digits;
};

/**
 * Convert 24h time string to 12h format, considering optional date & timezone
 * @param {string} time24h - e.g. "18:30"
 * @param {string} [date] - e.g. "2025-07-16"; if not provided, use today
 * @param {string} [timezone] - e.g. "America/New_York"; if not provided, use local timezone
 * @returns {string} time in 12h format, e.g. "06:30 PM"
 */
export const convertTimeTo12h = (time24h: string, date = "", timezone = "") => {
  // Use provided date or today's date in YYYY-MM-DD
  const dateStr = date
    ? moment(date).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  // Combine date + time
  const dateTimeStr = `${dateStr} ${time24h}`;

  let timeMoment;
  if (timezone) {
    // parse in the specified timezone
    timeMoment = moment.tz(dateTimeStr, "YYYY-MM-DD HH:mm", timezone);
  } else {
    // parse in local time
    timeMoment = moment(dateTimeStr, "YYYY-MM-DD HH:mm");
  }

  // Format as 12h with AM/PM
  return timeMoment.format("hh:mm A");
};

export const convertAvailabiltyArr = (
  data: Array<{ day: string; id: string; startTime: string; endTime: string }>
) => {
  const dayMap = {
    Sun: "Sunday",
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
  };

  const dayOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Group existing data
  const grouped = data.reduce((acc, { id, day, startTime, endTime }) => {
    if (!acc[day]) {
      acc[day] = {
        label: dayMap[day],
        day,
        hours: [],
      };
    }
    acc[day].hours.push({ id, startTime, endTime });
    return acc;
  }, {});

  // Ensure all 7 days exist (fill missing with empty hours)
  const fullWeek = dayOrder.map((d) => {
    return grouped[d] || { label: dayMap[d], day: d, hours: [] };
  });

  return fullWeek;
};

export const calculateAmount = (
  ratePerHour: number | string,
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string
): number => {
  console.log("Params :", ratePerHour, startDate, startTime, endDate, endTime);

  // Convert ratePerHour to a number safely
  const rate = parseFloat(ratePerHour as string);
  if (isNaN(rate)) {
    throw new Error("Invalid ratePerHour value");
  }

  // Create start and end moments
  const startDateTime = moment(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  const endDateTime = moment(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm");

  // Validate date parsing
  if (!startDateTime.isValid() || !endDateTime.isValid()) {
    throw new Error("Invalid date or time format");
  }

  // Calculate total duration in hours
  const durationInHours = moment
    .duration(endDateTime.diff(startDateTime))
    .asHours();

  // Ensure duration is not negative
  if (durationInHours < 0) {
    throw new Error("End time is before start time");
  }

  // Calculate total amount
  const amount = durationInHours * rate;

  return parseFloat(amount.toFixed(2));
};

export const formateNumber = (amount: number | string) => {
  const rate = parseFloat(amount as string);
  return parseFloat(rate.toFixed(2));
};

// Show area without trailing-zero decimals:
// 123.00 -> "123", 123.50 -> "123.5", 123.75 -> "123.75"
export const formatArea = (area: number | string | null | undefined) => {
  const value = parseFloat(area as string);
  if (isNaN(value)) {
    return "0";
  }
  return String(parseFloat(value.toFixed(2)));
};

// Tidy a free-typed address/location string: trim, collapse repeated spaces,
// remove the space before commas, ensure a single space after each comma, and
// Title-Case each word. e.g. "Kitengela ,nairobi" -> "Kitengela, Nairobi".
export const formatAddress = (
  address: string | null | undefined
): string => {
  if (!address) {
    return "";
  }

  return String(address)
    .trim()
    .replace(/\s+/g, " ") // collapse repeated whitespace
    .replace(/\s*,\s*/g, ", ") // normalize spacing around commas
    .split(" ")
    .map((word) =>
      word
        // Title-case across hyphens too (e.g. "san-jose" -> "San-Jose").
        .split("-")
        .map((part) =>
          part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
        )
        .join("-")
    )
    .join(" ")
    .trim();
};

// Show price with a leading "$", whole-dollar when there are no decimals:
// 0 / "" -> "$0", 50 -> "$50", 75.5 -> "$75.50", 75.25 -> "$75.25"
export const formatPrice = (price: number | string | null | undefined) => {
  const value = parseFloat(price as string);
  if (isNaN(value) || value === 0) {
    return "$0";
  }
  const rounded = parseFloat(value.toFixed(2));
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
};

export const parseAddressComponent = (
  components: Array<{ long_name: string; types: Array<String> }>
) => {
  const result = {
    country: "",
    state: "",
    city: "",
    postalCode: "",
  };

  components.forEach((component) => {
    const types = component.types;

    if (types.includes("country")) {
      result.country = component.long_name;
    }

    if (types.includes("administrative_area_level_1")) {
      result.state = component.long_name;
    }

    if (
      types.includes("locality") || // main city
      types.includes("sublocality") || // sub-city (like Brooklyn)
      types.includes("postal_town") // some countries use this
    ) {
      result.city = component.long_name;
    }

    if (types.includes("postal_code")) {
      result.postalCode = component.long_name;
    }
  });

  return result;
};

/**
 * Calculate duration in minutes between two times on the same or next day.
 * @param date - The base date (e.g., "2025-10-13")
 * @param startTime - Start time in 24h format (e.g., "14:18")
 * @param endTime - End time in 24h format (e.g., "17:45")
 * @returns Duration in minutes
 */
export const getDurationInMinutes = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  returnIn = "minutes"
): number => {
  // Create Date objects for start and end times
  const startDateTime = new Date(`${startDate}T${startTime}:00`);
  let endDateTime = new Date(`${endDate}T${endTime}:00`);

  // Handle case where end time is on the next day (e.g., overnight shift)
  if (endDateTime < startDateTime) {
    endDateTime.setDate(endDateTime.getDate() + 1);
  }

  // Calculate difference in milliseconds → minutes
  const diffMs = endDateTime.getTime() - startDateTime.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));

  if (returnIn === "seconds") {
    return diffMinutes * 60;
  }

  return diffMinutes;
};

export const convertToMinutes = (duration: string): number => {
  // Normalize input (trim + lowercase)
  const value = duration.trim().toLowerCase();

  // Match number part
  const num = parseFloat(value);

  console.log("Num :", num);

  // Handle minutes (check before "hour" so "minutes" isn't misread)
  if (value.includes("min")) {
    return num; // already minutes
  }

  // Handle hours
  if (value.includes("hour")) {
    return num * 60; // 1h = 60 mins
  }

  // Handle days
  if (value.includes("day")) {
    return num * 24 * 60; // 1 day = 1440 mins
  }

  // Default fallback
  return 0;
};

export const getTimeFrame = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  inSeconds = "no"
): string => {
  // Convert to Date objects
  const start = new Date(`${startDate}T${startTime}:00`);
  const end = new Date(`${endDate}T${endTime}:00`);

  // Get difference in milliseconds
  const diffMs = end - start;

  if (inSeconds === "yes") {
    return diffMs / 1000;
  }

  // Convert to total minutes
  const diffMinutes = Math.floor(diffMs / 60000);

  let output = "";

  if (diffMinutes < 60) {
    output = `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"}`;
  } else if (diffMinutes < 1440) {
    const hours = parseFloat(diffMinutes / 60).toFixed(0);

    output = `${hours} hour${hours === 1 || hours === "1" ? "" : "s"}`;

    console.log("Hours :", hours, output);
  } else {
    const days = parseFloat(diffMinutes / 1440).toFixed(0);
    output = `${days} day${days === 1 || days === "1" ? "" : "s"}`;
  }
  return output;
};
